export interface UserRoleFlags {
  isSuperadmin: boolean
  isSupport: boolean
  isCorporate: boolean
  isCore: boolean
  isTeam: boolean
  isPainter: boolean
  isAdmin: boolean
  isSales: boolean
  isCrew: boolean
  isViewer: boolean
  isFieldUser: boolean
  isOfficeUser: boolean
}

/**
 * Derive the boolean role flags for a user from their `app_metadata.roles`.
 *
 * This is the single source of truth for role-based UI gating. It is consumed by
 * `UserProvider` (which feeds `useUser()`), and can also be called directly with the
 * roles of an *arbitrary* user (e.g. the profile being edited in the Admin app, read
 * from Formik values) — contexts where there is no logged-in-user `UserProvider` for
 * that user.
 *
 * Notes on the legacy/new-style split:
 *  - `isAdmin`/`isSales`/`isCrew`/`isViewer` are legacy roles and are suppressed when the
 *    user also has a new-style role (`core`/`team`/`painter`), matching the original
 *    UserProvider logic.
 *  - `isFieldUser` (`crew || painter`) is the correct discriminator for "field user"
 *    gating, since modern painters use the `painter` role and are therefore NOT `isCrew`.
 */
export function getUserRoleFlags(roles: string[] = []): UserRoleFlags {
  const hasNewRole = roles.some((r) => r === 'core' || r === 'team' || r === 'painter')

  return {
    isSuperadmin: roles.includes('superadmin'),
    isSupport: roles.includes('support'),
    isCorporate: roles.includes('corporate'),
    isCore: roles.includes('core'),
    isTeam: roles.includes('team'),
    isPainter: roles.includes('painter'),
    isAdmin: roles.includes('admin') && !hasNewRole,
    isSales: roles.includes('sales') && !hasNewRole,
    isCrew: roles.includes('crew') && !hasNewRole,
    isViewer: roles.includes('viewer') && !hasNewRole,
    isFieldUser: roles.includes('crew') || roles.includes('painter'),
    isOfficeUser: roles.includes('viewer') || roles.includes('team')
  }
}
